/**
 * PlantHealthNotification - Notificação quando planta precisa de cuidados
 */
import React, { useEffect, useState } from 'react';
import { Droplets, AlertTriangle, X, Sparkles } from 'lucide-react';

const HEALTH_CONFIGS = {
  thirsty: {
    title: '💧 Sua Acácia está com sede',
    message: 'Que tal regar sua planta? Use seus regadores para mantê-la saudável!',
    color: '#3B82F6',
    bgColor: '#EFF6FF',
    borderColor: '#60A5FA',
    icon: Droplets,
    action: 'Regar Agora',
  },
  yellowing: {
    title: '⚠️ Folhas amarelando',
    message: 'Sua Acácia precisa de atenção! Continue sua jornada para recuperá-la.',
    color: '#F59E0B',
    bgColor: '#FEF3C7',
    borderColor: '#FCD34D',
    icon: AlertTriangle,
    action: 'Ver Trilha',
  },
  drying: {
    title: '🍂 Acácia ressecando',
    message: 'Urgente! Sua planta precisa de cuidado imediato. Retome sua jornada!',
    color: '#EF4444',
    bgColor: '#FEE2E2',
    borderColor: '#FCA5A5',
    icon: AlertTriangle,
    action: 'Agir Agora',
  },
};

export default function PlantHealthNotification({ healthState, onAction, onDismiss }) {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  useEffect(() => {
    // Show notification for unhealthy states
    if (['thirsty', 'yellowing', 'drying'].includes(healthState) && !dismissed) {
      setShow(true);
    } else {
      setShow(false);
    }
  }, [healthState, dismissed]);
  
  if (!show || !healthState || healthState === 'healthy') return null;
  
  const config = HEALTH_CONFIGS[healthState];
  if (!config) return null;
  
  const Icon = config.icon;
  
  const handleAction = () => {
    setShow(false);
    if (onAction) onAction();
  };
  
  const handleDismiss = () => {
    setShow(false);
    setDismissed(true);
    if (onDismiss) onDismiss();
  };
  
  return (
    <div 
      className="fixed top-4 right-4 z-50 max-w-sm w-full animate-slideIn"
      style={{ animation: 'slideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
    >
      <div
        className="rounded-2xl shadow-2xl p-5 border-2 relative overflow-hidden"
        style={{
          background: config.bgColor,
          borderColor: config.borderColor,
        }}
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 rounded-lg hover:bg-white/50 transition-colors"
          aria-label="Fechar"
        >
          <X className="w-4 h-4" style={{ color: config.color }} />
        </button>
        
        {/* Content */}
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div
            className="p-3 rounded-xl flex-shrink-0 shadow-sm"
            style={{ background: `${config.color}20` }}
          >
            <Icon className="w-6 h-6" style={{ color: config.color }} />
          </div>
          
          {/* Text */}
          <div className="flex-1 pr-6">
            <h3 
              className="font-bold text-base mb-1"
              style={{ color: config.color }}
            >
              {config.title}
            </h3>
            <p className="text-sm text-gray-700 mb-4 leading-relaxed">
              {config.message}
            </p>
            
            {/* Action button */}
            <button
              onClick={handleAction}
              className="px-4 py-2 rounded-lg font-semibold text-white text-sm shadow-md hover:shadow-lg transition-all transform hover:scale-105"
              style={{ background: config.color }}
            >
              {config.action}
            </button>
          </div>
        </div>
        
        {/* Animated pulse indicator */}
        <div
          className="absolute bottom-0 left-0 right-0 h-1 animate-pulse"
          style={{ background: config.color, opacity: 0.3 }}
        />
      </div>
      
      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .animate-slideIn {
          animation: slideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
}
